import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getContract } from "opnet";
import { useEscrowClient, type OpnetEscrowClient } from "./useEscrowClient";
import { MOTOSETTLE_CONFIG } from "../config";
import { OP20_ABI } from "../utils/opnetAbis";

// ─── OP20 helpers ─────────────────────────────────────────────────────────────

function getOp20Contract(client: OpnetEscrowClient, tokenAddr: string) {
  return getContract(
    tokenAddr as any,
    OP20_ABI,
    client.provider,
    client.network,
    client.senderAddress ?? undefined,
  );
}

export interface TokenMetadata {
  symbol: string;
  decimals: number;
  address: string;
}

export function useTokenMetadata(token: string | undefined) {
  const client = useEscrowClient();
  return useQuery<TokenMetadata | null>({
    queryKey: ["op20-metadata", token],
    queryFn: async () => {
      if (!client || !token) return null;
      // Skip zero/empty addresses
      if (/^0x0+$/.test(token.toLowerCase())) return null;
      try {
        const c: any = getOp20Contract(client, token);
        const [symRes, decRes] = await Promise.all([c.symbol(), c.decimals()]);
        const symbol = !symRes.revert ? (symRes.properties.symbol as string) : "???";
        const decimals = !decRes.revert ? Number(decRes.properties.decimals) : 8;
        return { symbol, decimals, address: token };
      } catch (e) {
        console.warn("Could not fetch token metadata for", token, e);
        return { symbol: "???", decimals: 8, address: token };
      }
    },
    enabled: !!client && !!token,
    staleTime: 5 * 60_000, // token metadata never changes
    gcTime: 30 * 60_000,
  });
}

/**
 * Format a raw bigint amount using the token's decimals.
 * @param maxFracDigits max decimals to show (default 2). Trailing zeros stripped.
 */
export function formatTokenAmount(
  amount: bigint,
  decimals: number,
  maxFracDigits: number = 2,
): string {
  if (decimals <= 0) return amount.toString();
  const divisor = 10n ** BigInt(decimals);
  const whole = amount / divisor;
  const frac = amount % divisor;
  if (frac === 0n) return whole.toString();
  let fracStr = frac.toString().padStart(decimals, "0");
  // Truncate to maxFracDigits
  if (maxFracDigits >= 0 && fracStr.length > maxFracDigits) {
    fracStr = fracStr.slice(0, maxFracDigits);
  }
  // Strip trailing zeros
  fracStr = fracStr.replace(/0+$/, "");
  return fracStr ? `${whole}.${fracStr}` : whole.toString();
}

// ─── UserReputation ──────────────────────────────────────────────────────────

export interface UserReputationStats {
  completedTrades: bigint;
  lifetimeVolume: bigint;
  disputesRaised: bigint;
  disputesWon: bigint;
  disputesLost: bigint;
  repScore: bigint;
  lastUpdated: bigint;
}

/**
 * Fetch on-chain reputation stats for a given user.
 * Returns null while client/user unavailable; zeros for unknown users (contract default).
 */
export function useUserReputation(user: string | undefined) {
  const client = useEscrowClient();
  return useQuery<UserReputationStats | null>({
    queryKey: ["user-reputation", user],
    queryFn: async () => {
      if (!client || !user) return null;
      const resolved = await client.provider.getPublicKeyInfo(user, true);
      if (!resolved) return null;
      const res = await client.userReputationContract.getStats(resolved);
      if (res.revert) throw new Error(res.revert);
      const p = res.properties;
      return {
        completedTrades: p.completedTrades as bigint,
        lifetimeVolume: p.lifetimeVolume as bigint,
        disputesRaised: p.disputesRaised as bigint,
        disputesWon: p.disputesWon as bigint,
        disputesLost: p.disputesLost as bigint,
        repScore: p.repScore as bigint,
        lastUpdated: p.lastUpdated as bigint,
      };
    },
    enabled: !!client && !!user,
    refetchInterval: 30_000,
  });
}

export function useTokenBalance(token: string | undefined, owner: string | undefined) {
  const client = useEscrowClient();
  return useQuery({
    queryKey: ["op20-balance", token, owner],
    queryFn: async () => {
      if (!client || !token || !owner) return 0n;
      const ownerAddr = await client.provider.getPublicKeyInfo(owner, true);
      if (!ownerAddr) return 0n;
      const c: any = getOp20Contract(client, token);
      const res = await c.balanceOf(ownerAddr);
      if (res.revert) throw new Error(res.revert);
      return res.properties.balance as bigint;
    },
    enabled: !!client && !!token && !!owner,
    refetchInterval: 15_000,
  });
}

export function useTokenAllowance(
  token: string | undefined,
  owner: string | undefined,
  spender: string | undefined,
) {
  const client = useEscrowClient();
  return useQuery({
    queryKey: ["op20-allowance", token, owner, spender],
    queryFn: async () => {
      if (!client || !token || !owner || !spender) return 0n;
      const [ownerAddr, spenderAddr] = await Promise.all([
        client.provider.getPublicKeyInfo(owner, true),
        client.provider.getPublicKeyInfo(spender, true),
      ]);
      if (!ownerAddr || !spenderAddr) return 0n;
      const c: any = getOp20Contract(client, token);
      const res = await c.allowance(ownerAddr, spenderAddr);
      if (res.revert) throw new Error(res.revert);
      return res.properties.remaining as bigint;
    },
    enabled: !!client && !!token && !!owner && !!spender,
    refetchInterval: 15_000,
  });
}

export function useApproveToken() {
  const client = useEscrowClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { token: string; spender: string; amount: bigint }) => {
      if (!client) throw new Error("Wallet not connected");
      const spenderAddr = await client.provider.getPublicKeyInfo(params.spender, true);
      if (!spenderAddr) throw new Error("No se pudo resolver la direccion del spender");
      const c: any = getOp20Contract(client, params.token);
      const sim = await c.increaseAllowance(spenderAddr, params.amount);
      if (sim.revert) throw new Error(sim.revert);
      return sim.sendTransaction(txParams(client));
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["op20-allowance", vars.token] });
    },
  });
}

// ─── Query keys ────────────────────────────────────────────────────────────────
const ESCROW_KEY = (id: string) => ["escrow", id] as const;
const PROTOCOL_STATS_KEY = ["protocol-stats"] as const;
const ARBITRATORS_KEY = ["arbitrators"] as const;

// ─── Default tx params ────────────────────────────────────────────────────────
function txParams(client: OpnetEscrowClient) {
  return {
    signer: client.signer ?? null,
    mldsaSigner: null,
    refundTo: client.walletAddress,
    maximumAllowedSatToSpend: 100_000n,
    feeRate: 10,
    network: client.network,
  };
}

// ─── On-chain reads ────────────────────────────────────────────────────────────

export function useEscrow(escrowId: bigint | undefined) {
  const client = useEscrowClient();
  return useQuery({
    queryKey: ESCROW_KEY(escrowId?.toString() ?? "0"),
    queryFn: async () => {
      const result = await client!.escrowContract.getEscrow(escrowId!);
      if (result.revert) throw new Error(result.revert);
      return result.properties;
    },
    enabled: !!client && escrowId !== undefined,
    refetchInterval: 15_000,
  });
}

// ─── Tx history lookup (on-chain + localStorage) ─────────────────────────────

export interface EscrowTxRecord {
  label: string;
  txId: string;
  blockNumber?: bigint;
  source: "on-chain" | "local";
}

// Local persistence of tx ids per escrow (browser-scoped; indexer will replace this).
const LS_KEY = (escrowId: string) => `motosettle:escrow-txs:${escrowId}`;

interface LocalTx {
  label: string;
  txId: string;
  timestamp: number;
}

export function saveEscrowTx(escrowId: bigint | string, label: string, txId: string) {
  try {
    const key = LS_KEY(escrowId.toString());
    const existing: LocalTx[] = JSON.parse(localStorage.getItem(key) || "[]");
    if (existing.some((t) => t.txId === txId)) return;
    existing.push({ label, txId, timestamp: Date.now() });
    localStorage.setItem(key, JSON.stringify(existing));
  } catch {
    // ignore storage errors
  }
}

function loadLocalEscrowTxs(escrowId: bigint | string): LocalTx[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY(escrowId.toString())) || "[]");
  } catch {
    return [];
  }
}

/**
 * Fetches tx history for an escrow by scanning the block where it was created.
 * Uses provider.getBlock(createdAt, prefetchTxs=true) and filters transactions
 * that target EscrowCore. 100% on-chain, no localStorage needed.
 */
export function useEscrowTxHistory(
  createdAtBlock: bigint | undefined,
  escrowId?: bigint,
) {
  const client = useEscrowClient();
  const escrowCoreAddr = MOTOSETTLE_CONFIG.escrowCore;

  return useQuery({
    queryKey: ["escrow-tx-history", createdAtBlock?.toString(), escrowId?.toString()],
    queryFn: async (): Promise<EscrowTxRecord[]> => {
      const records: EscrowTxRecord[] = [];

      // 1. On-chain creation tx (from createdAt block scan)
      if (client && createdAtBlock && createdAtBlock > 0n) {
        try {
          const block = await client.provider.getBlock(createdAtBlock, true);
          if (block && block.transactions) {
            const escrowAddrLower = escrowCoreAddr.toLowerCase();
            for (const tx of block.transactions) {
              const contractAddr = (tx as any).contractAddress as string | undefined;
              if (!contractAddr) continue;
              if (contractAddr.toLowerCase() === escrowAddrLower) {
                const txId = tx.id || tx.hash;
                if (txId) {
                  records.push({
                    label: "Create",
                    txId,
                    blockNumber: createdAtBlock,
                    source: "on-chain",
                  });
                }
              }
            }
          }
        } catch (err) {
          console.warn("Could not fetch tx history for block", createdAtBlock.toString(), err);
        }
      }

      // 2. Local tx history (Fund, Activate, Release, etc.) — saved by mutations
      if (escrowId !== undefined) {
        const local = loadLocalEscrowTxs(escrowId);
        for (const l of local) {
          // Skip if already present (e.g. Create from on-chain)
          if (records.some((r) => r.txId === l.txId)) continue;
          records.push({ label: l.label, txId: l.txId, source: "local" });
        }
      }

      return records;
    },
    enabled: !!client && (createdAtBlock !== undefined || escrowId !== undefined),
    staleTime: 30_000,
  });
}

export function useComputeFee(token: string | undefined, amount: bigint, motoBalance = 0n) {
  const client = useEscrowClient();
  return useQuery({
    queryKey: ["fee", token, amount.toString(), motoBalance.toString()],
    queryFn: async () => {
      const result = await client!.feePolicyContract.computeFee(
        client!.walletAddress,
        amount,
        motoBalance,
      );
      if (result.revert) throw new Error(result.revert);
      return result.properties.fee as bigint;
    },
    enabled: !!client && !!token && amount > 0n,
    staleTime: 10_000,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────────

export interface CreateAndFundParams {
  seller: string;
  arbitrator: string;
  token: string;
  amount: bigint;
  fundDeadline: bigint;
  tradeDeadline: bigint;
  motoBalance: bigint;
}

export function useCreateAndFundEscrow() {
  const client = useEscrowClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateAndFundParams): Promise<bigint> => {
      if (!client) throw new Error("Wallet not connected");

      // Resolve bech32 addresses to Address objects via RPC
      const [sellerAddr, arbitratorAddr, tokenAddr] = await Promise.all([
        client.provider.getPublicKeyInfo(params.seller, true),
        client.provider.getPublicKeyInfo(params.arbitrator, true),
        client.provider.getPublicKeyInfo(params.token, true),
      ]);

      if (!sellerAddr) throw new Error("No se pudo resolver la direccion del seller");
      if (!arbitratorAddr) throw new Error("No se pudo resolver la direccion del arbitrador");
      if (!tokenAddr) throw new Error("No se pudo resolver la direccion del token");

      // Call EscrowCore.createEscrow directly (bypasses Router)
      const simulation = await client.escrowContract.createEscrow(
        sellerAddr,
        arbitratorAddr,
        tokenAddr,
        params.amount,
        params.fundDeadline,
        params.tradeDeadline,
      );

      if ("error" in simulation) {
        throw new Error(`Contract error: ${simulation.error}`);
      }

      if (simulation.revert) {
        throw new Error(`Transaction would revert: ${simulation.revert}`);
      }

      // Sign and broadcast via wallet
      const receipt = await simulation.sendTransaction(txParams(client));
      const escrowId = simulation.properties.escrowId as bigint;

      if (receipt?.transactionId) saveEscrowTx(escrowId, "Create", receipt.transactionId);
      console.log("Escrow created, tx:", receipt.transactionId);
      return escrowId;
    },
    onSuccess: (escrowId) => {
      queryClient.invalidateQueries({ queryKey: ESCROW_KEY(escrowId.toString()) });
      queryClient.invalidateQueries({ queryKey: PROTOCOL_STATS_KEY });
    },
  });
}

export interface FundEscrowParams {
  escrowId: bigint;
  motoBalance: bigint;
  token: string;
  total: bigint; // amount + fee (approve target)
  onStep?: (step: "check" | "approve" | "waitApprove" | "fund" | "done") => void;
}

export function useFundEscrow() {
  const client = useEscrowClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: FundEscrowParams) => {
      if (!client) throw new Error("Wallet not connected");
      params.onStep?.("check");

      // Resolve addresses
      const [ownerAddr, spenderAddr] = await Promise.all([
        client.provider.getPublicKeyInfo(client.walletAddress, true),
        client.provider.getPublicKeyInfo(MOTOSETTLE_CONFIG.escrowCore, true),
      ]);
      if (!ownerAddr || !spenderAddr) throw new Error("No se pudo resolver direcciones");

      // Check balance
      const op20: any = getOp20Contract(client, params.token);
      const balRes = await op20.balanceOf(ownerAddr);
      if (balRes.revert) throw new Error(`balanceOf: ${balRes.revert}`);
      const balance = balRes.properties.balance as bigint;
      if (balance < params.total) {
        throw new Error(
          `Balance insuficiente: tienes ${balance}, se requieren ${params.total} (amount + fee)`,
        );
      }

      // Check allowance
      const alwRes = await op20.allowance(ownerAddr, spenderAddr);
      if (alwRes.revert) throw new Error(`allowance: ${alwRes.revert}`);
      const allowance = alwRes.properties.remaining as bigint;

      // Approve if insufficient — OP20 uses increaseAllowance (delta, not absolute)
      if (allowance < params.total) {
        params.onStep?.("approve");
        const delta = params.total - allowance;
        const approveSim = await op20.increaseAllowance(spenderAddr, delta);
        if (approveSim.revert) throw new Error(`increaseAllowance: ${approveSim.revert}`);
        await approveSim.sendTransaction(txParams(client));

        // Poll until the allowance is visible on-chain (tx confirmed)
        params.onStep?.("waitApprove");
        const maxAttempts = 60; // ~2 minutes at 2s interval
        let confirmed = false;
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const check = await op20.allowance(ownerAddr, spenderAddr);
          if (!check.revert && (check.properties.remaining as bigint) >= params.total) {
            confirmed = true;
            break;
          }
        }
        if (!confirmed) {
          throw new Error(
            "El approve no se confirmo on-chain tras 2min. Recarga e intenta fondear de nuevo (el approve ya esta firmado).",
          );
        }
      }

      // Fund
      params.onStep?.("fund");
      const sim = await client.escrowContract.fundEscrow(params.escrowId, params.motoBalance);
      if (sim.revert) throw new Error(sim.revert);
      const receipt = await sim.sendTransaction(txParams(client));
      if (receipt?.transactionId) saveEscrowTx(params.escrowId, "Fund", receipt.transactionId);
      params.onStep?.("done");
      return receipt;
    },
    onSuccess: (_, { escrowId, token }) => {
      queryClient.invalidateQueries({ queryKey: ESCROW_KEY(escrowId.toString()) });
      queryClient.invalidateQueries({ queryKey: ["op20-balance", token] });
      queryClient.invalidateQueries({ queryKey: ["op20-allowance", token] });
      queryClient.invalidateQueries({ queryKey: ["escrow-tx-history"] });
    },
  });
}

function makeEscrowMutation(
  action: string,
  call: (client: OpnetEscrowClient, arg: any) => Promise<any>,
  getId: (arg: any) => bigint,
  invalidateStats = false,
) {
  return function useHook() {
    const client = useEscrowClient();
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (arg: any) => {
        if (!client) throw new Error("Wallet not connected");
        const sim = await call(client, arg);
        if (sim.revert) throw new Error(sim.revert);
        const receipt = await sim.sendTransaction(txParams(client));
        if (receipt?.transactionId) {
          saveEscrowTx(getId(arg), action, receipt.transactionId);
        }
        return receipt;
      },
      onSuccess: (_, arg) => {
        const id = getId(arg);
        queryClient.invalidateQueries({ queryKey: ESCROW_KEY(id.toString()) });
        queryClient.invalidateQueries({ queryKey: ["escrow-tx-history"] });
        if (invalidateStats) queryClient.invalidateQueries({ queryKey: PROTOCOL_STATS_KEY });
      },
    });
  };
}

export const useActivateEscrow = makeEscrowMutation(
  "Activate",
  (c, id: bigint) => c.escrowContract.activateEscrow(id),
  (id: bigint) => id,
);

export const useReleaseFunds = makeEscrowMutation(
  "Release",
  (c, id: bigint) => c.escrowContract.releaseFunds(id),
  (id: bigint) => id,
  true,
);

export const useRaiseDispute = makeEscrowMutation(
  "Dispute",
  (c, id: bigint) => c.escrowContract.raiseDispute(id),
  (id: bigint) => id,
);

export const useResolveDispute = makeEscrowMutation(
  "Resolve",
  (c, p: { escrowId: bigint; buyerPct: number }) =>
    c.escrowContract.resolveDispute(p.escrowId, BigInt(p.buyerPct)),
  (p: { escrowId: bigint }) => p.escrowId,
  true,
);

export const useExpireEscrow = makeEscrowMutation(
  "Expire",
  (c, id: bigint) => c.escrowContract.expireEscrow(id),
  (id: bigint) => id,
);

// ─── Escrow browsing ──────────────────────────────────────────────────────────

export function useEscrowCount() {
  const client = useEscrowClient();
  return useQuery({
    queryKey: ["escrow-count"],
    queryFn: async () => {
      const result = await client!.escrowContract.escrowCount();
      if (result.revert) throw new Error(result.revert);
      return result.properties.count as bigint;
    },
    enabled: !!client,
    refetchInterval: 15_000,
  });
}

export interface EscrowData {
  id: bigint;
  buyer: string;
  seller: string;
  arbitrator: string;
  token: string;
  amount: bigint;
  fee: bigint;
  fundDeadline: bigint;
  tradeDeadline: bigint;
  createdAt: bigint;
  state: number;
  buyerResolved: bigint;
  sellerResolved: bigint;
}

// IMPORTANT: These must match EscrowCore.ts STATE_* constants exactly.
// contracts-opnet/src/contracts/EscrowCore.ts (lines 48-54):
//   0 = CREATED, 1 = FUNDED, 2 = ACTIVE, 3 = RELEASED,
//   4 = DISPUTED, 5 = RESOLVED, 7 = EXPIRED (note: 6 is unused)
export const STATE_CREATED = 0;
export const STATE_FUNDED = 1;
export const STATE_ACTIVE = 2;
export const STATE_RELEASED = 3;
export const STATE_DISPUTED = 4;
export const STATE_RESOLVED = 5;
export const STATE_EXPIRED = 7;

const STATE_NAMES: Record<number, string> = {
  0: "Created",
  1: "Funded",
  2: "Active",
  3: "Released",
  4: "Disputed",
  5: "Resolved",
  7: "Expired",
};

export function getStateName(state: number): string {
  return STATE_NAMES[state] ?? `Unknown(${state})`;
}

export function getStateColor(state: number): string {
  switch (state) {
    case STATE_CREATED:  return "var(--text-muted)";
    case STATE_FUNDED:   return "var(--warning)";
    case STATE_ACTIVE:   return "var(--info)";
    case STATE_RELEASED: return "var(--success)";
    case STATE_DISPUTED: return "var(--danger)";
    case STATE_RESOLVED: return "var(--success)";
    case STATE_EXPIRED:  return "var(--text-muted)";
    default: return "var(--text-muted)";
  }
}

/**
 * Fetches all escrows from 1..count by querying getEscrow(id) for each.
 * Since AddressMemoryMap is not iterable on-chain, we must fetch all and filter client-side.
 */
export function useAllEscrows() {
  const client = useEscrowClient();
  const { data: count } = useEscrowCount();

  return useQuery({
    queryKey: ["all-escrows", count?.toString()],
    queryFn: async (): Promise<EscrowData[]> => {
      if (!client || !count || count === 0n) return [];

      const escrows: EscrowData[] = [];
      // Fetch in parallel, batches of 5 to avoid overwhelming RPC
      const total = Number(count);
      for (let batch = 0; batch < total; batch += 5) {
        const batchEnd = Math.min(batch + 5, total);
        const promises = [];
        for (let i = batch; i < batchEnd; i++) {
          const id = BigInt(i + 1); // escrow IDs are 1-based
          promises.push(
            client.escrowContract.getEscrow(id).then((result: any) => {
              if (result.revert) return null;
              const p = result.properties;
              return {
                id,
                buyer: p.buyer?.toString?.() ?? p.buyer ?? "",
                seller: p.seller?.toString?.() ?? p.seller ?? "",
                arbitrator: p.arbitrator?.toString?.() ?? p.arbitrator ?? "",
                token: p.token?.toString?.() ?? p.token ?? "",
                amount: p.amount as bigint,
                fee: p.fee as bigint,
                fundDeadline: p.fundDeadline as bigint,
                tradeDeadline: p.tradeDeadline as bigint,
                createdAt: p.createdAt as bigint,
                state: Number(p.state),
                buyerResolved: p.buyerResolved as bigint,
                sellerResolved: p.sellerResolved as bigint,
              } as EscrowData;
            }).catch(() => null),
          );
        }
        const results = await Promise.all(promises);
        for (const r of results) {
          if (r) escrows.push(r);
        }
      }

      return escrows;
    },
    enabled: !!client && count !== undefined && count > 0n,
    refetchInterval: 20_000,
    staleTime: 10_000,
  });
}

// ─── Stubs for hooks used by other pages ─────────────────────────────────────

export function useUserStats(_address: string | undefined) {
  return useQuery({ queryKey: ["user-stats"], queryFn: async () => null, enabled: false });
}

export function useArbitratorInfo(_address: string | undefined) {
  return useQuery({ queryKey: ["arb-info"], queryFn: async () => null, enabled: false });
}

export function useActiveArbitrators() {
  return useQuery({ queryKey: ARBITRATORS_KEY, queryFn: async () => [], enabled: false });
}

export interface ProtocolStats {
  totalEscrows: bigint;
  activeEscrows: bigint; // Created + Funded + Active + Disputed
  totalVolume: bigint;   // sum of amount across all
  totalFees: bigint;     // sum of fee across Released + Resolved
  totalDisputes: bigint;
  totalResolved: bigint;
  totalReleased: bigint;
  totalExpired: bigint;
}

/**
 * Computes protocol stats client-side from all escrows.
 * TODO: replace with indexer query (ProtocolStats entity) when The Graph is deployed.
 */
export function useProtocolStats(): { data: ProtocolStats | null; isLoading: boolean } {
  const { data: escrows, isLoading } = useAllEscrows();

  if (!escrows) return { data: null, isLoading };

  const stats: ProtocolStats = {
    totalEscrows: 0n,
    activeEscrows: 0n,
    totalVolume: 0n,
    totalFees: 0n,
    totalDisputes: 0n,
    totalResolved: 0n,
    totalReleased: 0n,
    totalExpired: 0n,
  };

  for (const e of escrows) {
    // Skip empty (zero-initialized) slots
    if (/^0x0+$/.test(e.buyer.toLowerCase()) && e.amount === 0n && e.createdAt === 0n) continue;

    stats.totalEscrows += 1n;
    stats.totalVolume += e.amount;

    const s = e.state;
    if (s === STATE_CREATED || s === STATE_FUNDED || s === STATE_ACTIVE || s === STATE_DISPUTED) {
      stats.activeEscrows += 1n;
    }
    if (s === STATE_RELEASED || s === STATE_RESOLVED) {
      stats.totalFees += e.fee;
    }
    if (s === STATE_DISPUTED || s === STATE_RESOLVED) stats.totalDisputes += 1n;
    if (s === STATE_RESOLVED) stats.totalResolved += 1n;
    if (s === STATE_RELEASED) stats.totalReleased += 1n;
    if (s === STATE_EXPIRED) stats.totalExpired += 1n;
  }

  return { data: stats, isLoading };
}

export function useEscrowsByBuyer(_buyer: string | undefined) {
  return useQuery({ queryKey: ["escrows-buyer"], queryFn: async () => [], enabled: false });
}

export function useRegisterArbitrator() {
  return useMutation({ mutationFn: async (_feeRateBps: bigint) => {} });
}
